/**
 * PWWCapital — Client-side Audit Logger (Section 15)
 *
 * Sends structured audit events to the backend audit endpoint.
 * Events are queued and flushed so they never block the UI.
 *
 * Every entry includes: userId, timestamp, action, result,
 * and basic device/IP metadata the backend appends server-side.
 */

import api from '../services/api';

/** @typedef {'login'|'logout'|'transfer'|'payment'|'approval'|'permission_change'|'profile_update'|'card_action'|'document_upload'|'session_revoke'} AuditAction */
/** @typedef {'success'|'failure'|'blocked'} AuditResult */

const QUEUE_FLUSH_INTERVAL_MS = 3000;
const MAX_QUEUE_SIZE = 20;

let queue = [];
let flushTimer = null;

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, QUEUE_FLUSH_INTERVAL_MS);
}

async function flush() {
  flushTimer = null;
  if (!queue.length) return;

  const batch = queue.splice(0, queue.length);
  try {
    await api.post('/audit/events/', { events: batch });
  } catch {
    // Audit failures are silent — never disrupt user flow.
    // Events are lost; server-side logging is the authoritative source.
  }
}

/**
 * Log a client-side audit event.
 *
 * @param {AuditAction} action
 * @param {AuditResult} result
 * @param {Record<string, unknown>} [metadata]
 */
export function auditLog(action, result = 'success', metadata = {}) {
  // Never log sensitive field values — only action type & result
  const event = {
    action,
    result,
    timestamp: new Date().toISOString(),
    user_agent: navigator.userAgent,
    // Screen session fingerprint (non-identifying, for anomaly detection)
    session_id: getSessionId(),
    ...sanitizeMetadata(metadata),
  };

  queue.push(event);

  if (queue.length >= MAX_QUEUE_SIZE) {
    flush();
  } else {
    scheduleFlush();
  }
}

/** Get or create a short session ID stored in sessionStorage (cleared on tab close) */
function getSessionId() {
  try {
    let id = sessionStorage.getItem('_pww_sid');
    if (!id) {
      id = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      sessionStorage.setItem('_pww_sid', id);
    }
    return id;
  } catch {
    return 'unknown';
  }
}

/** Strip any accidental sensitive keys before logging */
function sanitizeMetadata(meta) {
  const FORBIDDEN_KEYS = ['password', 'pin', 'cvv', 'ssn', 'token', 'secret', 'otp'];
  const safe = {};
  for (const [k, v] of Object.entries(meta)) {
    if (!FORBIDDEN_KEYS.some((fk) => k.toLowerCase().includes(fk))) {
      safe[k] = v;
    }
  }
  return safe;
}

// Flush on page unload (best-effort)
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (queue.length) flush();
  });
}
